import { Grid } from '@mui/material';
import { LanguageType, SearchPredicate, validLanguages } from '@nx-smartmonkey/shared/domain';
import { dateFormatShortDate, toDayEnd } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalPalette,
  RoutalSearchInputSelector,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
} from '@nx-smartmonkey/ui';
import _ from 'lodash';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { Range } from 'react-date-range';

import { useSearchUsersPredicates } from '../../../../../hooks/backoffice/users/useSearchUsersPredicates';
import Button from '../../../../../react-components/Button';
import Text from '../../../../../react-components/Text';
import { DateRangePicker } from '../../../../../react-components/date-range-picker/DateRangePicker';
import { getFlagByLocale } from '../../../shared';
import { BackofficeClientsUsersListColumnsButton } from './BackofficeClientsUsersListColumnsButton';

const searchTextFields = [`email`, `first_name`, `last_name`, `phone`];

export const BackofficeClientsUsersListFilters = () => {
  const { predicates, setPredicates } = useSearchUsersPredicates();

  const [datePickerPosition, setDatePickerPosition] = useState<{ top: number; left: number } | undefined>(undefined);

  const startDatePredicate = predicates.find((p) => p.field === `created_at` && p.operator === `gte`);
  const startDate = useMemo(
    () =>
      startDatePredicate?.value
        ? new Date(startDatePredicate.value as string)
        : moment(new Date()).subtract(`5`, `year`).toDate(),
    [startDatePredicate]
  );
  const endDatePredicate = predicates.find((p) => p.field === `created_at` && p.operator === `lte`);
  const endDate = useMemo(
    () => (endDatePredicate?.value ? new Date(endDatePredicate.value as string) : new Date()),
    [endDatePredicate]
  );

  const languagePredicate = predicates.find(
    (p) => p.field === `language` && !!p.value && p.value !== `` && validLanguages.includes(p.value as LanguageType)
  );
  const language = (languagePredicate?.value ?? undefined) as LanguageType | undefined;

  const searchTextPredicate = predicates.find((p) => searchTextFields.includes(p.field));
  const searchText = (searchTextPredicate?.value ?? ``) as string;
  const [searchField, setSearchField] = useState<string>(searchTextPredicate?.field || `email`);

  return (
    <>
      <Grid container direction="column">
        <Grid item container direction="row" justifyContent="flex-end" alignItems="center">
          <Button
            id="date-picker-button"
            styleType="secondary"
            variant="flat"
            size="xs"
            style={{
              backgroundColor: `transparent`,
              height: `26px`,
              borderColor: RoutalPalette.neutral20,
              borderRadius: `5px`,
            }}
            onClick={(e: any) => {
              const datePickerButton = document.getElementById(`date-picker-button`);
              if (!datePickerButton) return;

              const { x, bottom } = datePickerButton.getBoundingClientRect();
              setDatePickerPosition({ top: bottom, left: x });
            }}
          >
            <Grid container item justifyContent="space-between" direction="row" wrap="nowrap" alignItems="center">
              <Grid item>
                <Text>{`${dateFormatShortDate(startDate)} - ${dateFormatShortDate(endDate)}`}</Text>
              </Grid>
            </Grid>
          </Button>

          <HSpacer small />

          <RoutalSelect
            id="filter-language"
            containerStyle={{
              width: `230px`,
            }}
            popoverProps={{
              options: [
                {
                  key: `all_languages`,
                  onClick: () => {
                    setPredicates([...predicates.filter((p) => p.field !== `language`)]);
                  },
                  content: (
                    <Grid
                      key="all_languages-column"
                      item
                      container
                      direction="row"
                      wrap="nowrap"
                      justifyContent="flex-start"
                      alignItems="center"
                    >
                      <Text style={{ cursor: `pointer` }} intlId="administration.translations.language.all" />
                    </Grid>
                  ),
                },
                ...validLanguages.map(
                  (validLanguage): RoutalSelectMultiSelectOption => ({
                    key: validLanguage,
                    onClick: () => {
                      setPredicates([
                        ...predicates.filter((p) => p.field !== `language`),
                        SearchPredicate.create({
                          field: `language`,
                          operator: `eq`,
                          type: `string`,
                          value: validLanguage,
                        }),
                      ]);
                    },
                    content: (
                      <Grid
                        key={`${validLanguage}-column`}
                        item
                        container
                        direction="row"
                        wrap="nowrap"
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <img src={getFlagByLocale(validLanguage)} alt="" width="20px" draggable="false" />
                        <HSpacer small />
                        <Text
                          style={{ cursor: `pointer` }}
                          intlId={`administration.translations.language.${validLanguage}`}
                        />
                      </Grid>
                    ),
                  })
                ),
              ],
            }}
          >
            <Grid item container direction="row" wrap="nowrap" justifyContent="flex-start" alignItems="center">
              {language ? (
                <>
                  <img src={getFlagByLocale(language)} alt="" width="20px" draggable="false" />
                  <HSpacer small />
                  <Text style={{ cursor: `pointer` }} intlId={`administration.translations.language.${language}`} />
                </>
              ) : (
                <Text style={{ cursor: `pointer` }} intlId="administration.translations.language.all" />
              )}
            </Grid>
          </RoutalSelect>

          <HSpacer small />

          <Grid container direction="row" justifyContent="flex-end" wrap="nowrap" style={{ maxWidth: `320px` }}>
            <RoutalSearchInputSelector
              id={`search-users`}
              onTextChange={_.debounce((text: any) => {
                console.info(`onTextChange`, text, searchField);
                setPredicates([
                  ...predicates.filter((p) => !searchTextFields.includes(p.field)),
                  ...(text
                    ? [
                        SearchPredicate.create({
                          field: searchField,
                          operator: `contains`,
                          type: `string`,
                          value: text,
                        }),
                      ]
                    : []),
                ]);
              }, 300)}
              searchText={searchText}
              options={searchTextFields.map(
                (field): RoutalSelectMultiSelectOption => ({
                  key: `search-selector-button-${field}`,
                  width: 250,
                  checked: field === searchField,
                  content: <Text intlId={`backoffice.clients.users.${field}`} />,
                  onClick: () => {
                    setSearchField(field);
                    const predicate = predicates.filter((p) => searchTextFields.includes(p.field));
                    const text = predicate.length > 0 ? predicate[0].value : undefined;
                    if (text) {
                      setPredicates([
                        ...predicates.filter((p) => !searchTextFields.includes(p.field)),
                        ...(text
                          ? [
                              SearchPredicate.create({
                                field,
                                operator: `contains`,
                                type: `string`,
                                value: text,
                              }),
                            ]
                          : []),
                      ]);
                    }
                  },
                })
              )}
            />
          </Grid>
        </Grid>

        <Grid item container justifyContent="flex-end">
          <BackofficeClientsUsersListColumnsButton id="backoffice-users-configurable-table-columns" />
        </Grid>
      </Grid>

      <DateRangePicker
        id="select-dates"
        popoverPosition={datePickerPosition}
        startDate={startDate ? new Date(startDate) : undefined}
        endDate={endDate ? new Date(endDate) : undefined}
        onClose={() => {
          setDatePickerPosition(undefined);
        }}
        onUpdate={(range: Range) => {
          setPredicates([
            ...predicates.filter((p) => p.field !== `created_at`),
            ...(range.startDate
              ? [
                  SearchPredicate.create({
                    field: `created_at`,
                    operator: `gte`,
                    type: `date`,
                    value: range.startDate.toUTCString(),
                  }),
                ]
              : []),
            ...(range.endDate
              ? [
                  SearchPredicate.create({
                    field: `created_at`,
                    operator: `lte`,
                    type: `date`,
                    value: toDayEnd(range.endDate).toUTCString(),
                  }),
                ]
              : []),
          ]);
        }}
      />
    </>
  );
};
